import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@istari-frontend/ui";
import { KeyRound } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";

import { clearLlmApiKey, closeLlmKeyPrompt, setLlmApiKey, useLlmApiKey } from "~/ai-chat/data-source/llm-api-key-store";

/**
 * Prompt for the user's own LLM key. Opened on `token_expired` (the stored key
 * was rejected) or manually. The key is held for this browser session only
 * (see `llm-api-key-store`), never persisted to Istari.
 */
export function LlmApiKeyDialog() {
  const { promptOpen, hasKey } = useLlmApiKey();
  const [value, setValue] = useState("");
  const inputId = useId();

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    setLlmApiKey(trimmed);
    setValue("");
  };

  const removeKey = () => {
    clearLlmApiKey();
    toast.success("LLM key removed.");
    setValue("");
    closeLlmKeyPrompt();
  };

  return (
    <Dialog
      open={promptOpen}
      onOpenChange={(open) => {
        if (!open) {
          setValue("");
          closeLlmKeyPrompt();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-4" />
            Enter your LLM key
          </DialogTitle>
          <DialogDescription>
            Kept in this browser tab only for the current session — never stored on Istari servers, and cleared when you
            log out.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={inputId}>LLM key</Label>
          <Input
            id={inputId}
            type="password"
            autoComplete="off"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
            placeholder="Paste your key…"
          />
        </div>
        <DialogFooter>
          {hasKey ? (
            <Button variant="destructive" onClick={removeKey}>
              Remove key
            </Button>
          ) : null}
          <Button onClick={submit} disabled={value.trim().length === 0}>
            Save key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
